import React, { useState } from 'react';
import { Badge, Box, Avatar, Tooltip, Typography, Stack, useMediaQuery, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import ChevronDownIcon from './icons/ChevronDownIcon';

import { useAuth } from '../contexts/AuthContext';
import { useReleaseNotesStore } from '../hooks/useReleaseNotesStore';
import ProfileMenu from './ui/header/ProfileMenu';

const UserProfileMenu: React.FC = observer(() => {
	const { user, signOut } = useAuth();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { versionNotifications } = useReleaseNotesStore();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Box sx={{ pr: isMobile ? 0.5 : 1 }}>
				<Box
					onClick={handleMenuOpen}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1.5,
						cursor: 'pointer',
						p: 1,
						borderRadius: 1,
						transition: 'background-color 0.2s',
					}}
				>
					<Tooltip title={versionNotifications ? "A new app release has been published since your last login" : ""}>
						<Badge
							variant="dot"
							overlap="circular"
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							invisible={!versionNotifications}
							sx={{
								'& .MuiBadge-dot': {
									backgroundColor: '#C77DFF',
									width: 10,
									height: 10,
									borderRadius: '50%',
									border: `2px solid ${theme.palette.background.paper}`,
								},
							}}
						>
							<Avatar
								src={user?.backend?.picture || "/assets/avatar.png"}
								alt="User Avatar"
								sx={{
									width: 40,
									height: 40,
								}}
							/>
						</Badge>
					</Tooltip>
					{!isMobile && (
						<Stack direction="column" spacing={0.125} alignItems="flex-start">
							<Typography
								variant="subtitle1"
								sx={{
									color: theme.palette.text.primary,
									fontWeight: 600,
									lineHeight: 1.25,
									whiteSpace: 'nowrap',
								}}
							>
								{user?.backend?.name || user?.backend?.username}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: theme.palette.text.secondary,
									fontWeight: 500,
									lineHeight: 1,
									whiteSpace: 'nowrap',
								}}
							>
								{user?.backend?.email}
							</Typography>

							{
								user?.backend?.current_tenant?.name ? 
									<Typography
										variant="body2"
										sx={{
											color: theme.palette.text.secondary,
											fontWeight: 700,
											lineHeight: 1,
											marginTop: '6px !important',
											whiteSpace: 'nowrap',
										}}
									>
										{user?.backend?.current_tenant?.name}
									</Typography> : null
							}
						</Stack>
					)}

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							color: theme.palette.text.secondary,
							transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 0.2s',
							'& svg': {
								width: 10,
								height: 10,
							},
						}}
					>
						<ChevronDownIcon className="" />
					</Box>
				</Box>

				<ProfileMenu
					anchorEl={anchorEl}
					onClose={handleMenuClose}
					onLogout={() => {
						handleMenuClose();
						void signOut();
					}}
				/>
			</Box>
		</>
	);
});

export default UserProfileMenu;


